// Camada de métricas puras do Dashboard administrativo.
//
// Cada função aqui é pura (sem I/O, sem side-effects) e documenta
// explicitamente de qual campo real do schema ela deriva o número —
// nenhuma métrica aqui foi inventada; todas foram confirmadas lendo
// AddImovel.js/EditImovel.js/ListaImoveis.js e validadas contra dados reais
// do Firebase (ver relatório da tarefa "Redesign do dashboard").

// ---------------------------------------------------------------------------
// Helpers de campo
// ---------------------------------------------------------------------------

// Preço vem sempre como string BRL formatada em informacoesComerciais.valor
// (ex.: "R$ 350.000,00"). Quando o imóvel foi fechado pela Senna, existe
// também imovelInfo.statusInfo.valorVenda já como number.
export function parsePriceBRL(valor) {
  if (typeof valor === "number") return Number.isFinite(valor) ? valor : NaN;
  if (typeof valor !== "string" || !valor.trim()) return NaN;
  const n = parseFloat(valor.replace(/[^\d]/g, "")) / 100;
  return Number.isFinite(n) ? n : NaN;
}

export function median(values) {
  const arr = [...values].filter(Number.isFinite).sort((a, b) => a - b);
  if (!arr.length) return null;
  const mid = Math.floor(arr.length / 2);
  return arr.length % 2 ? arr[mid] : (arr[mid - 1] + arr[mid]) / 2;
}

// Imóvel ativo: imovelInfo.status === true (campo real de disponibilidade;
// false = fechado/vendido/alugado/desativado, ver imovelInfo.statusInfo.tipo).
export function isActive(imovel) {
  return imovel?.imovelInfo?.status === true;
}

// Fechamento "pela Senna": imovelInfo.status === false E
// imovelInfo.statusInfo.tipo === "vendido_senna" (preenchido manualmente no
// modal de desativação em ListaImoveis.js). "desativado" sem esse tipo NÃO
// conta como fechamento (pode ser remoção por outro motivo).
export function isClosedBySenna(imovel) {
  return (
    imovel?.imovelInfo?.status === false &&
    imovel?.imovelInfo?.statusInfo?.tipo === "vendido_senna"
  );
}

// Valor de fechamento: usa statusInfo.valorVenda quando existe (preenchido
// no momento do fechamento), senão cai para o valor de tabela do imóvel.
// IMPORTANTE: isto é o valor do NEGÓCIO (venda/aluguel), não a comissão da
// imobiliária — não existe campo de comissão confiável hoje (ver relatório).
export function getDealValue(imovel) {
  const statusInfo = imovel?.imovelInfo?.statusInfo;
  if (statusInfo?.valorVenda !== undefined && statusInfo?.valorVenda !== null) {
    const n =
      typeof statusInfo.valorVenda === "number"
        ? statusInfo.valorVenda
        : parsePriceBRL(statusInfo.valorVenda);
    if (Number.isFinite(n)) return n;
  }
  return parsePriceBRL(imovel?.informacoesComerciais?.valor);
}

// Imóvel sem foto: imagesLinks é um objeto indexado ("0","1",...), não um
// array — precisa checar Object.values, não .length direto.
export function hasNoPhotos(imovel) {
  const imgs = imovel?.imagesLinks;
  if (!imgs || typeof imgs !== "object") return true;
  return Object.values(imgs).filter(Boolean).length === 0;
}

export function hasNoPrice(imovel) {
  const n = parsePriceBRL(imovel?.informacoesComerciais?.valor);
  return !Number.isFinite(n) || n <= 0;
}

// "Sem localização" = falta cidade OU bairro — são os dois campos que
// alimentam os filtros de cidade/bairro do site público.
export function hasNoLocation(imovel) {
  const cidade = imovel?.imovelInfo?.cidade;
  const bairro = imovel?.imovelInfo?.bairro;
  return !cidade || !String(cidade).trim() || !bairro || !String(bairro).trim();
}

// Corretor captador (quem cadastrou o imóvel) — único campo de corretor
// preenchido em 100% dos registros reais auditados.
export function getCaptadorId(imovel) {
  return imovel?.imovelInfo?.corretor || null;
}

// Corretor do FECHAMENTO — statusInfo.corretor (preenchido no modal de
// "vendido pela Senna"), com fallback para o captador quando ausente. O
// dashboard antigo usava só o captador para todo o ranking; aqui corrigimos
// isso: quando existe um corretor de fechamento registrado, é ele quem
// recebe o crédito pelo fechamento.
export function getClosingCorretorId(imovel) {
  return imovel?.imovelInfo?.statusInfo?.corretor || getCaptadorId(imovel);
}

// ---------------------------------------------------------------------------
// Datas / períodos
// ---------------------------------------------------------------------------

export function startOfWeek(d = new Date()) {
  const tmp = new Date(d);
  const day = tmp.getDay() === 0 ? 7 : tmp.getDay();
  tmp.setHours(0, 0, 0, 0);
  tmp.setDate(tmp.getDate() - (day - 1));
  return tmp;
}

export function startOfMonth(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function startOfDaysAgo(days, d = new Date()) {
  const tmp = new Date(d);
  tmp.setDate(tmp.getDate() - (days - 1));
  tmp.setHours(0, 0, 0, 0);
  return tmp;
}

export function periodStartFor(period, ref = new Date()) {
  if (period === "WEEK") return startOfWeek(ref);
  if (period === "MONTH") return startOfMonth(ref);
  return startOfDaysAgo(90, ref);
}

// Janela imediatamente anterior, de mesma duração — usada para calcular
// variação percentual só onde há timestamp confiável (dataStatus).
export function previousPeriodRange(period, ref = new Date()) {
  const currentStart = periodStartFor(period, ref);
  const durationMs = ref.getTime() - currentStart.getTime();
  const previousEnd = new Date(currentStart.getTime());
  const previousStart = new Date(currentStart.getTime() - durationMs);
  return { start: previousStart, end: previousEnd };
}

function inRange(dateVal, start, end) {
  if (!dateVal) return false;
  const t = new Date(dateVal).getTime();
  if (!Number.isFinite(t)) return false;
  if (end) return t >= start.getTime() && t < end.getTime();
  return t >= start.getTime();
}

// ---------------------------------------------------------------------------
// Métricas de estoque
// ---------------------------------------------------------------------------

// Universo: TODOS os imóveis em /imoveis (ativos + inativos). "Ativos" é o
// subconjunto com imovelInfo.status === true — é isso que deve aparecer como
// KPI principal, não o total bruto (que inclui vendidos/desativados).
export function calculateInventoryMetrics(imoveis) {
  const total = imoveis.length;
  const active = imoveis.filter(isActive);
  const inactive = total - active.length;

  const byModalidadeActive = { Venda: 0, Aluguel: 0, Temporada: 0 };
  active.forEach((i) => {
    const m = i.imovelInfo?.modalidade;
    if (m && byModalidadeActive[m] !== undefined) byModalidadeActive[m] += 1;
  });

  return {
    total,
    activeCount: active.length,
    inactiveCount: inactive,
    byModalidadeActive,
  };
}

// ---------------------------------------------------------------------------
// Saúde do estoque (só considera imóveis ATIVOS — não faz sentido "corrigir"
// um imóvel já fechado/desativado).
// ---------------------------------------------------------------------------

export function calculateDataHealth(imoveis) {
  const active = imoveis.filter(isActive);

  const semFoto = active.filter(hasNoPhotos);
  const semPreco = active.filter(hasNoPrice);
  const semLocalizacao = active.filter(hasNoLocation);

  // união (um imóvel pode ter mais de um problema, mas conta uma vez)
  const withAnyIssue = new Set(
    [...semFoto, ...semPreco, ...semLocalizacao].map((i) => i.id)
  );

  return {
    totalActive: active.length,
    semFoto,
    semPreco,
    semLocalizacao,
    totalComProblema: withAnyIssue.size,
  };
}

// ---------------------------------------------------------------------------
// Integridade /imoveis x /imoveisPublicos
// ---------------------------------------------------------------------------

export function calculateIntegrityIssues(imoveis, imoveisPublicosMap) {
  const pubMap = imoveisPublicosMap || {};
  const privateById = new Map(imoveis.map((i) => [i.id, i]));
  const privateIds = new Set(privateById.keys());
  const publicIds = new Set(Object.keys(pubMap));

  // Existe em /imoveisPublicos mas não existe mais em /imoveis — órfão
  // publicado no site sem registro de origem (ex.: apagado direto no banco).
  const orphanPublic = [...publicIds]
    .filter((id) => !privateIds.has(id))
    .map((id) => ({
      id,
      codigo: pubMap[id]?.imovelInfo?.codigo || null,
      titulo: pubMap[id]?.imovelInfo?.titulo || null,
    }));

  // Existe em /imoveis (ativo) mas nunca foi publicado em /imoveisPublicos —
  // só é problema se o imóvel estiver ativo (fechados não precisam estar públicos).
  const missingPublic = imoveis
    .filter((i) => isActive(i) && !publicIds.has(i.id))
    .map((i) => ({ id: i.id, codigo: i.imovelInfo?.codigo || null, titulo: i.imovelInfo?.titulo || null }));

  // status diverge entre os dois nós para o mesmo id.
  const statusMismatch = [];
  privateIds.forEach((id) => {
    if (!publicIds.has(id)) return;
    const sPriv = privateById.get(id)?.imovelInfo?.status;
    const sPub = pubMap[id]?.imovelInfo?.status;
    if (sPriv !== sPub) {
      statusMismatch.push({
        id,
        codigo: privateById.get(id)?.imovelInfo?.codigo || null,
        titulo: privateById.get(id)?.imovelInfo?.titulo || null,
        statusPrivado: sPriv,
        statusPublico: sPub,
      });
    }
  });

  const totalIssues = orphanPublic.length + missingPublic.length + statusMismatch.length;
  const totalChecked = new Set([...privateIds, ...publicIds]).size;

  return {
    orphanPublic,
    missingPublic,
    statusMismatch,
    totalIssues,
    syncedCount: totalChecked - totalIssues,
  };
}

// ---------------------------------------------------------------------------
// Distribuição de portfólio (só imóveis ATIVOS — é "como está meu estoque
// hoje", não histórico)
// ---------------------------------------------------------------------------

export function calculatePortfolioDistribution(imoveis) {
  const active = imoveis.filter(isActive);

  const porModalidade = { Venda: 0, Aluguel: 0, Temporada: 0 };
  const porTipo = {};
  const porCidade = {};

  active.forEach((i) => {
    const m = i.imovelInfo?.modalidade;
    if (m && porModalidade[m] !== undefined) porModalidade[m] += 1;

    const tipo = i.imovelInfo?.tipo?.trim() || "Não informado";
    porTipo[tipo] = (porTipo[tipo] || 0) + 1;

    const cidade = i.imovelInfo?.cidade?.trim() || "Não informado";
    porCidade[cidade] = (porCidade[cidade] || 0) + 1;
  });

  const tipoRanking = Object.entries(porTipo)
    .map(([tipo, count]) => ({ tipo, count }))
    .sort((a, b) => b.count - a.count);

  const cidadeRanking = Object.entries(porCidade)
    .map(([cidade, count]) => ({ cidade, count }))
    .sort((a, b) => b.count - a.count);

  const precosVenda = active
    .filter((i) => i.imovelInfo?.modalidade === "Venda")
    .map((i) => parsePriceBRL(i.informacoesComerciais?.valor))
    .filter(Number.isFinite);

  const precosAluguel = active
    .filter((i) => i.imovelInfo?.modalidade === "Aluguel")
    .map((i) => parsePriceBRL(i.informacoesComerciais?.valor))
    .filter(Number.isFinite);

  return {
    porModalidade,
    tipoRanking,
    cidadeRanking,
    precoVenda: {
      count: precosVenda.length,
      median: median(precosVenda),
      min: precosVenda.length ? Math.min(...precosVenda) : null,
      max: precosVenda.length ? Math.max(...precosVenda) : null,
    },
    precoAluguel: {
      count: precosAluguel.length,
      median: median(precosAluguel),
      min: precosAluguel.length ? Math.min(...precosAluguel) : null,
      max: precosAluguel.length ? Math.max(...precosAluguel) : null,
    },
  };
}

// ---------------------------------------------------------------------------
// Fechamentos (venda/aluguel "vendido pela Senna", agrupados por período)
// ---------------------------------------------------------------------------

const WEEKDAYS_PT = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

export function calculateClosings(imoveis, period, ref = new Date()) {
  const start = periodStartFor(period, ref);
  const closed = imoveis.filter(isClosedBySenna);

  const inPeriod = closed.filter((i) => inRange(i.imovelInfo?.dataStatus, start));
  const vendas = inPeriod.filter((i) => i.imovelInfo?.modalidade === "Venda");
  const alugueis = inPeriod.filter((i) => i.imovelInfo?.modalidade === "Aluguel");
  const volumeNegociado = inPeriod.reduce((acc, i) => acc + (getDealValue(i) || 0), 0);

  const { start: prevStart, end: prevEnd } = previousPeriodRange(period, ref);
  const prevCount = closed.filter((i) =>
    inRange(i.imovelInfo?.dataStatus, prevStart, prevEnd)
  ).length;
  const currentCount = inPeriod.length;
  const variacaoPct =
    prevCount > 0 ? ((currentCount - prevCount) / prevCount) * 100 : null;

  // barras por dia útil, só dentro da semana corrente (independente do período
  // selecionado — é sempre "esta semana", como um recorte operacional fixo)
  const weekStart = startOfWeek(ref);
  const porDia = WEEKDAYS_PT.slice(0, 5).map((name) => ({ name, vendas: 0, alugueis: 0 }));
  closed.forEach((i) => {
    const raw = i.imovelInfo?.dataStatus;
    if (!raw || !inRange(raw, weekStart)) return;
    const d = new Date(raw);
    const wd = d.getDay() === 0 ? 6 : d.getDay() - 1;
    const label = WEEKDAYS_PT[wd];
    const bucket = porDia.find((b) => b.name === label);
    if (!bucket) return; // sáb/dom fora do gráfico de dias úteis
    if (i.imovelInfo?.modalidade === "Venda") bucket.vendas += 1;
    if (i.imovelInfo?.modalidade === "Aluguel") bucket.alugueis += 1;
  });

  // volume negociado nos últimos 6 meses (para o gráfico de evolução)
  const last6 = [];
  for (let k = 5; k >= 0; k--) {
    const d = new Date(ref.getFullYear(), ref.getMonth() - k, 1);
    last6.push({
      key: `${d.getFullYear()}-${d.getMonth() + 1}`,
      label: `${d.toLocaleString("pt-BR", { month: "short" })}/${String(d.getFullYear()).slice(-2)}`,
      volume: 0,
    });
  }
  closed.forEach((i) => {
    const raw = i.imovelInfo?.dataStatus;
    if (!raw) return;
    const d = new Date(raw);
    const key = `${d.getFullYear()}-${d.getMonth() + 1}`;
    const bucket = last6.find((m) => m.key === key);
    if (bucket) bucket.volume += getDealValue(i) || 0;
  });

  return {
    vendas: vendas.length,
    alugueis: alugueis.length,
    total: currentCount,
    volumeNegociado,
    variacaoPct,
    porDia,
    volumeUltimos6Meses: last6,
  };
}

// ---------------------------------------------------------------------------
// Desempenho de corretores
// ---------------------------------------------------------------------------

export function calculateBrokerMetrics(imoveis, corretoresMap) {
  const byBroker = {};

  const ensure = (id) => {
    if (!byBroker[id]) {
      byBroker[id] = { id, ativosVenda: 0, ativosAluguel: 0, ativos: 0, fechamentos: 0, volumeFechado: 0 };
    }
    return byBroker[id];
  };

  imoveis.forEach((i) => {
    if (isActive(i)) {
      const captadorId = getCaptadorId(i) || "sem-corretor";
      const entry = ensure(captadorId);
      entry.ativos += 1;
      if (i.imovelInfo?.modalidade === "Venda") entry.ativosVenda += 1;
      if (i.imovelInfo?.modalidade === "Aluguel") entry.ativosAluguel += 1;
    }
    if (isClosedBySenna(i)) {
      const closerId = getClosingCorretorId(i) || "sem-corretor";
      const entry = ensure(closerId);
      entry.fechamentos += 1;
      entry.volumeFechado += getDealValue(i) || 0;
    }
  });

  return Object.values(byBroker)
    .map((entry) => {
      const c = corretoresMap?.[entry.id];
      const nome = c
        ? `${c.nome ?? ""} ${c.sobrenome ?? ""}`.trim()
        : entry.id === "sem-corretor"
          ? "Sem corretor"
          : entry.id;
      return { ...entry, nome };
    })
    .sort((a, b) => b.ativos - a.ativos);
}

// ---------------------------------------------------------------------------
// Atividade recente (a partir de /logs — só registra EDIÇÕES; não há log de
// criação, exclusão ou mudança de status hoje, então o feed reflete apenas
// edições reais registradas, nunca é inventado a partir de outro campo)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Aniversariantes da semana (feature pré-existente, preservada) — deriva de
// ownerInfo.birth ("dd/mm/aaaa") em cada imóvel. Como não existe uma
// entidade "cliente" única, o mesmo proprietário com vários imóveis pode
// aparecer mais de uma vez.
// ---------------------------------------------------------------------------

export function calculateBirthdays(imoveis, ref = new Date()) {
  const start = startOfWeek(ref);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);

  const result = [];
  imoveis.forEach((i) => {
    const birth = i?.ownerInfo?.birth;
    if (!birth) return;
    const parts = birth.split(/[/-]/);
    if (parts.length < 2) return;
    const dia = parseInt(parts[0], 10);
    const mes = parseInt(parts[1], 10);
    if (Number.isNaN(dia) || Number.isNaN(mes)) return;

    const aniversarioEsteAno = new Date(ref.getFullYear(), mes - 1, dia);
    if (aniversarioEsteAno >= start && aniversarioEsteAno <= end) {
      result.push({
        nome: i.ownerInfo?.nome || "Sem nome",
        telefone: i.ownerInfo?.telefone || "—",
        data: `${String(dia).padStart(2, "0")}/${String(mes).padStart(2, "0")}`,
      });
    }
  });
  return result;
}

export function calculateActivityFeed(logEntries, limit = 10) {
  return [...logEntries]
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, limit)
    .map((entry) => ({
      timestamp: entry.timestamp,
      codigo: entry.codigo,
      imovelId: entry.imovelId,
      fields: entry.fields,
    }));
}
